import { Link, useRouterState } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useMemo, useRef } from 'react';

import { useEntitlements } from '@/lib/use-entitlements';
import { useTprmLink } from '@/lib/use-connected-products';
import { canOpen, useCaller } from '@/lib/access';
import { decidePage, pageById, type Caller } from '@/generated/persona/discovery-access';
import { MODULE_LABELS, SUPPORT_EMAIL, notInPlanSentence, planLabel } from '@/lib/plan-modules';
import { LicenceStatusNotice } from './LicenceStatusNotice';
import {
  Laptop,
  FolderTree,
  Radar,
  Activity,
  ShieldCheck,
  ShieldAlert,
  ExternalLink,
  Fingerprint,
  Sparkles,
  LayoutDashboard,
  Search,
  GitBranch,
  ListTree,
  Network,
  AlertTriangle,
  FileSearch,
  FileCheck2,
  AppWindow,
  Activity as Stream,
  TrendingUp,
  Waves,
  Radio,
  Timer,
  Gavel,
  Workflow,
  ScrollText,
  KeyRound,
  Database,
  ListChecks,
  Grid3x3,
  Atom,
  Lock,
  Cpu,
  Brain,
  History,
  Zap,
  MessageSquare,
  Layers,
  ShieldOff,
  Home,
  Settings,
  Plug,
  BadgeCheck,
  ScanLine,
  Users as UsersIcon,
  FileLock2,
  Building2,
  Hash,
  Award,
  Server,
  Edit3,
  Pause,
  Download,
  Megaphone,
  ClipboardCheck,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Mark } from '@/components/brand/Mark';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, ScrollArea, Separator } from '@/components/ui/primitives';
import { useEffect, useState } from 'react';
import { getBootstrapProgress } from '@/lib/api';

interface NavItem {
  /**
   * The persona specification id of the page (release 3.1, D6/X0). Whether the
   * item is offered comes ONLY from the generated persona table for this id
   * (`canOpen`): the specification cell for the caller's role, the permissions
   * the page's calls need, and the tenant's licences. Also the item's
   * `data-nav-id` test hook (SPEC.md 9.1).
   */
  pageId: string;
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  /**
   * P1-9 + P1-12 (2026-05-26) - sub-section header rendered above the
   * item when this value differs from the previous item's `section`.
   * Lets a single flat array still render as visually-grouped without
   * changing the NavGroup type or the activeGroup state model.
   */
  section?: string;
}

/**
 * P1 OBS-3.alt (2026-05-26) - surface a dynamic "{N}/8" badge on the
 * 8-Level Bootstrap nav item so it actually reflects state instead
 * of the static "L1-L8" descriptor. Cache module-level so SideNav
 * re-renders (every nav transition) don't spam the bootstrap-progress
 * endpoint. One fetch per page load, refresh on app reload.
 */
let BOOTSTRAP_CACHE: { activeCount: number; ts: number } | null = null;
let BOOTSTRAP_INFLIGHT: Promise<void> | null = null;

function useBootstrapBadge(allowed: boolean): string {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    // Release 3.1 D6: a role that cannot open the checklist is not asked for
    // its progress (the call would 403).
    if (!allowed) return;
    // Skip if we've fetched in the last 5 minutes - covers the
    // common "user clicks around for a while" session pattern.
    if (BOOTSTRAP_CACHE && Date.now() - BOOTSTRAP_CACHE.ts < 5 * 60_000) return;
    if (BOOTSTRAP_INFLIGHT) {
      void BOOTSTRAP_INFLIGHT.then(() => setTick((t) => t + 1));
      return;
    }
    BOOTSTRAP_INFLIGHT = getBootstrapProgress()
      .then((p) => {
        BOOTSTRAP_CACHE = { activeCount: p.summary.completeCount ?? 0, ts: Date.now() };
      })
      .catch(() => {
        // Network failure → keep static fallback. Don't poison
        // the cache so we retry next page load.
      })
      .finally(() => {
        BOOTSTRAP_INFLIGHT = null;
        setTick((t) => t + 1);
      });
  }, [allowed]);
  // Re-render-driven by tick + we always read the latest cache value.
  void tick;
  return BOOTSTRAP_CACHE ? `${BOOTSTRAP_CACHE.activeCount}/8` : 'L1-L8';
}

interface NavGroup {
  id: 'discovery' | 'activity' | 'detection' | 'compliance' | 'frontier' | 'admin';
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  /**
   * 2026-05-27 - clicking the module-switcher pill should navigate to
   * that section's canonical landing page (Discovery → /discovery
   * dashboard, Detection → /detection operator dashboard, etc.), not
   * just toggle the active group. Falls back to the first item's `to`
   * when not specified.
   */
  defaultHref?: string;
  items: NavItem[];
}

/**
 * The sidebar: groups, order, icons and labels. WHO SEES WHICH ITEM is not
 * here: each item names its specification page, and the generated persona
 * table decides (release 3.1, D6). A group whose items are all hidden is not
 * shown. The licence of each module is the page's (the table carries the nav
 * group's modules), which is what the group-level `requiresAnyModule` used to
 * say.
 */
export const NAV: NavGroup[] = [
  // 2026-05-27 - Welcome & Setup is no longer a top-level group.
  // The onboarding checklist still lives at /welcome (and the route
  // is registered in router.tsx) but is reached via Settings & Admin
  // → Onboarding checklist. The Discovery Dashboard at / (HomePage)
  // is the post-login landing page; brand mark + post-login flows
  // all target / now.
  {
    id: 'discovery',
    label: 'Discovery',
    icon: Radar,
    accent: 'from-chart-1 to-chart-2',
    defaultHref: '/discovery',
    // doc 33 IA: split daily-operational pages ("Operate") from
    // setup/explainer pages ("Set up") so a SOC analyst's daily surface
    // isn't diluted by config/how-it-works pages. Uses the existing
    // `section` sub-header mechanism - Discovery-scoped, no shell change.
    items: [
      { pageId: 'disc.dspm_dashboard', to: '/dspm', label: 'Dashboard', icon: LayoutDashboard, section: 'Operate' },
      { pageId: 'disc.dspm_scans', to: '/dspm/scans', label: 'Scans', icon: Radar, section: 'Operate' },
      { pageId: 'disc.dspm_findings', to: '/dspm/findings', label: 'Findings', icon: AlertTriangle, section: 'Operate' },
      // What approved Consent DPIAs declared, and whether each element's scan rule is in effect.
      { pageId: 'disc.dspm_declared', to: '/dspm/declared-elements', label: 'DPIA data elements', icon: FileCheck2, section: 'Operate' },
      { pageId: 'disc.dspm_inventory', to: '/dspm/inventory', label: 'Data Catalog', icon: Database, section: 'Operate' },
      // Release 2.1 Gate B items 16 and 14: the laptop fleet at 13,000-laptop scale.
      { pageId: 'disc.dspm_fleet', to: '/dspm/fleet', label: 'Endpoint Agents', icon: Laptop, section: 'Operate' },
      { pageId: 'disc.dspm_laptops', to: '/dspm/laptops', label: 'Endpoint Data', icon: FolderTree, section: 'Operate' },
      { pageId: 'disc.dspm_time_travel', to: '/dspm/time-travel', label: 'Posture Trends', icon: History, section: 'Operate' },
      { pageId: 'disc.dspm_lineage', to: '/dspm/lineage', label: 'Data Lineage', icon: GitBranch, section: 'Operate' },
      // Sits beside Data Lineage because it answers a lineage question: which
      // views, procedures, functions and SQL Agent job steps reach a given
      // column. Labelled for the job, not the table it reads.
      { pageId: 'disc.dspm_sql_objects', to: '/dspm/sql-objects', label: 'Impact Analysis', icon: ListTree, section: 'Operate' },
      { pageId: 'disc.dspm_section9', to: '/dspm/section-9', label: "Children's data (Sec 9)", icon: FileSearch, badge: 'India', section: 'Operate' },
      { pageId: 'disc.dspm_reverse_dsar', to: '/dspm/reverse-dsar', label: 'Data Subject Search', icon: Search, section: 'Operate' },
      // Slice D1 (3 Nov 2026): which application each data source and asset belongs to.
      { pageId: 'disc.dspm_applications', to: '/dspm/applications', label: 'Applications', icon: AppWindow, section: 'Inventory' },
      // Honey-PII Canaries is intentionally hidden from the default sidebar
      // (Phase 0 Day 5 - 2026-05-22). The page remains reachable via direct
      // URL `/discovery/honey-pii` for tenants experimenting with deception
      // canaries, but it's not a Day-1 BFSI/the customer customer surface.
      { pageId: 'disc.dspm_connectors', to: '/dspm/connectors', label: 'Connectors', icon: Plug, section: 'Set up' },
      // "Deploy a scanner" is NOT a separate nav line (founder 2026-07-11):
      // it lives INSIDE the Scan targets page as a CTA to the provisioning
      // wizard, so the scan-setup flow is one place, not two.
      { pageId: 'disc.dspm_targets', to: '/dspm/target-builder', label: 'Scan targets', icon: Radar, section: 'Set up' },
      { pageId: 'disc.dspm_bootstrap', to: '/dspm/bootstrap', label: 'Getting Started', icon: ListChecks, badge: 'L1-L8', section: 'Set up' },
      { pageId: 'disc.dspm_classifier', to: '/dspm/classifier', label: 'Classifiers', icon: Cpu, badge: '5x', section: 'Set up' },
      { pageId: 'disc.dspm_threat_sigs', to: '/dspm/threat-signatures', label: 'Threat signatures', icon: ShieldAlert, badge: 'Malware', section: 'Set up' },
      { pageId: 'disc.dspm_edm', to: '/dspm/edm', label: 'Exact Data Match (EDM)', icon: Fingerprint, badge: 'PII', section: 'Set up' },
      { pageId: 'disc.dspm_no_db', to: '/dspm/no-db-access', label: 'Scan without credentials', icon: ShieldOff, badge: 'BFSI', section: 'Set up' },
    ],
  },
  {
    id: 'activity',
    label: 'Activity',
    icon: Activity,
    accent: 'from-chart-2 to-chart-7',
    defaultHref: '/activity',
    items: [
      { pageId: 'disc.dam_dashboard', to: '/activity', label: 'Dashboard', icon: LayoutDashboard },
      { pageId: 'disc.dam_sources', to: '/activity/sources', label: 'DAM Sources', icon: Database },
      { pageId: 'disc.dam_stream', to: '/activity/stream', label: 'Live Query Stream', icon: Stream },
      { pageId: 'disc.dam_findings', to: '/activity/findings', label: 'Live Findings', icon: TrendingUp, badge: 'Live' },
      { pageId: 'disc.dam_ueba', to: '/activity/ueba', label: 'UEBA Baselines', icon: TrendingUp },
      { pageId: 'disc.dam_sonar', to: '/activity/sonar', label: 'Submarine Sonar', icon: Waves },
      { pageId: 'disc.dam_pwave', to: '/activity/p-wave', label: 'P-Wave Indicators', icon: Radio },
      // 2026-05-29 - surface the cross-engine kill-chain (compound correlator) + the
      // forensic timeline page that existed but was missing from nav (undiscoverable).
      { pageId: 'disc.dam_kill_chain', to: '/activity/kill-chain', label: 'Kill-Chain Findings', icon: Zap, badge: 'Fusion' },
      { pageId: 'disc.dam_timeline', to: '/activity/timeline', label: 'Forensic Timeline', icon: History },
      { pageId: 'disc.dam_breach_readiness', to: '/activity/breach-readiness', label: 'Breach Readiness', icon: Timer, badge: 'RBI 6h' },
    ],
  },
  {
    id: 'detection',
    label: 'Detection',
    icon: ShieldCheck,
    accent: 'from-chart-7 to-chart-1',
    defaultHref: '/detection',
    items: [
      { pageId: 'disc.ddr_dashboard', to: '/detection', label: 'Operator Dashboard', icon: LayoutDashboard },
      { pageId: 'disc.ddr_decisions', to: '/detection/decisions', label: 'Decisions', icon: Gavel },
      { pageId: 'disc.ddr_rules', to: '/detection/rules', label: 'Rules', icon: ScrollText },
      { pageId: 'disc.ddr_playbooks', to: '/detection/playbooks', label: 'Playbooks', icon: Workflow },
      { pageId: 'disc.ddr_break_glass', to: '/detection/break-glass', label: 'Break-Glass', icon: KeyRound, badge: '2P' },
      { pageId: 'disc.ddr_siem', to: '/detection/siem-gallery', label: 'SIEM / SOAR', icon: Grid3x3, badge: '29' },
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance',
    icon: ShieldCheck,
    accent: 'from-chart-1 to-chart-7',
    defaultHref: '/compliance',
    items: [
      // P1-9 (2026-05-26) - 14 flat compliance items grouped into 4
      // sub-sections so an evaluator can scan the surface at a glance:
      //   • Overview         - Compliance Center, Regulatory Coverage
      //   • Subject rights   - Notices, Erasure, Rectification,
      //                        Restriction, Portability, Objection
      //                        (the GDPR/DPDPA articles for individual
      //                        data subjects)
      //   • Risk & sensitivity - DPIA, Sensitive-PI tiers, Service
      //                          providers (Art 28)
      //   • Regional + incidents - CCPA opt-outs, SDF (DPDPA §10),
      //                            Breach dispatch
      { pageId: 'disc.comp_center', to: '/compliance', label: 'Compliance Center', icon: ShieldCheck, section: 'Overview' },
      { pageId: 'disc.comp_coverage', to: '/compliance/regulatory-coverage', label: 'Regulatory Coverage', icon: Award, section: 'Overview' },
      { pageId: 'disc.comp_notices', to: '/compliance/notices', label: 'Notices', icon: FileLock2, section: 'Subject rights' },
      { pageId: 'disc.comp_erasure', to: '/compliance/erasure', label: 'Erasure (Art 17)', icon: ShieldOff, section: 'Subject rights' },
      { pageId: 'disc.comp_rectification', to: '/compliance/rectification', label: 'Rectification (Art 16)', icon: Edit3, section: 'Subject rights' },
      { pageId: 'disc.comp_restriction', to: '/compliance/restriction', label: 'Restriction (Art 18)', icon: Pause, section: 'Subject rights' },
      // P1-8 (2026-05-26) - these 7 features shipped weeks ago (each
      // page exists at apps/web-shell/src/pages/compliance/{name}.tsx).
      // The 'new' badge was reused without expiry and is now stale
      // clutter for an evaluator. Removed wholesale; if any of these
      // ever materially changes, the badge can be added back with a
      // dated expiry.
      { pageId: 'disc.comp_portability', to: '/compliance/portability', label: 'Portability (Art 20)', icon: Download, section: 'Subject rights' },
      { pageId: 'disc.comp_objection', to: '/compliance/objection', label: 'Objection (Art 21)', icon: Megaphone, section: 'Subject rights' },
      { pageId: 'disc.comp_dpia', to: '/compliance/dpia', label: 'DPIA (Art 35)', icon: ClipboardCheck, section: 'Risk & sensitivity' },
      { pageId: 'disc.comp_sensitive', to: '/compliance/sensitive-pi', label: 'Sensitive-PI tiers', icon: Lock, section: 'Risk & sensitivity' },
      { pageId: 'disc.comp_providers', to: '/compliance/service-providers', label: 'Service providers (Art 28)', icon: Building2, section: 'Risk & sensitivity' },
      { pageId: 'disc.comp_ccpa', to: '/compliance/ccpa', label: 'CCPA opt-outs (§1798.120)', icon: ShieldOff, section: 'Regional + incidents' },
      { pageId: 'disc.comp_sdf', to: '/compliance/sdf', label: 'SDF (DPDPA §10)', icon: Award, section: 'Regional + incidents' },
      { pageId: 'disc.comp_breaches', to: '/compliance/breaches', label: 'Breach dispatch (Art 33)', icon: AlertTriangle, section: 'Regional + incidents' },
    ],
  },
  // Frontier (Sovereign-tier) group - gated behind `frontier` entitlement
  // which no production tenant has by default today. Routes still exist
  // for direct-URL access by curious operators / sales demos; the nav
  // only renders for tenants explicitly entitled to Frontier features.
  // Per Phase 0 audit (docs/master-plan/19-POST-PHASE-0-AUDIT-REPORT.md):
  // these pages are D-grade placeholders, hidden from default nav until
  // Phase 4 builds them properly. Sovereign-tier customers get the flag.
  {
    id: 'frontier',
    label: 'Frontier',
    icon: Sparkles,
    accent: 'from-chart-2 to-chart-7',
    defaultHref: '/frontier',
    items: [
      { pageId: 'disc.frontier', to: '/frontier', label: 'Overview', icon: LayoutDashboard },
      { pageId: 'disc.frontier_federated', to: '/frontier/federated', label: 'Federated Learning', icon: Brain },
      { pageId: 'disc.frontier_smpc', to: '/frontier/smpc', label: 'SMPC Benchmarks', icon: Network, badge: 'k≥3' },
      { pageId: 'disc.frontier_pq', to: '/frontier/pq', label: 'Post-Quantum Vault', icon: Lock },
      { pageId: 'disc.frontier_sensors', to: '/frontier/sensors', label: 'eBPF / Network Tap', icon: Cpu },
      { pageId: 'disc.frontier_byo_llm', to: '/frontier/byo-llm', label: 'BYO-LLM (Tier 5)', icon: Atom, badge: '9' },
      { pageId: 'disc.frontier_chat', to: '/frontier/chat', label: 'AI Assistant', icon: MessageSquare },
      { pageId: 'disc.frontier_replay', to: '/frontier/replay', label: 'Compliance Replay', icon: History },
    ],
  },
  {
    // BFSI-grade discoverability: operators logging in for the first
    // time MUST be able to find Settings / Integrations from the side
    // nav without knowing a URL. Per Session #34 (2026-05-13) UX gap.
    id: 'admin',
    label: 'Settings & Admin',
    icon: Settings,
    accent: 'from-slate-400 to-slate-600',
    // 2026-05-27 - explicit landing so the pill click doesn't
    // accidentally route to whatever happens to be the first
    // item in the list (which moved over time).
    defaultHref: '/admin/integrations',
    items: [
      // P1-12 (2026-05-26) - 18 flat admin items grouped into 4
      // sub-sections by intent:
      //   • Integrations    - connectors + Core pairing + JWT verifier
      //                       (anything that wires Discovery to other
      //                       systems)
      //   • Identity & access - Business Units, DPO & Grievance,
      //                         Compliance Posture, RBAM Matrix,
      //                         Custom Roles, Purposes (who can do
      //                         what, on whose behalf)
      //   • Policy & catalog - Adapters, Licenses, DAM Retention,
      //                        Industry Packs, Classifier Vocab,
      //                        India Moats (tenant-shaping config)
      //   • Edge & audit    - Edge Fleet, Provision pod, Egress Log
      //                       (operations + tamper-evidence)
      { pageId: 'disc.adm_integrations', to: '/admin/integrations', label: 'Integrations', icon: Plug, badge: 'BFSI', section: 'Integrations' },
      // Phase 2 - the configure-once shared data-source registry (Discovery/DAM/DDR all consume).
      { pageId: 'disc.adm_data_sources', to: '/admin/data-sources', label: 'Data Sources', icon: Database, section: 'Integrations' },
      // Phase 0 - what each module minimally needs (the module-relevance matrix).
      { pageId: 'disc.adm_module_setup', to: '/admin/module-setup', label: 'Module Setup', icon: ListChecks, section: 'Integrations' },
      { pageId: 'disc.adm_core', to: '/admin/integrations/foctta-core', label: '→ FOCTTA Privacy Core', icon: ShieldCheck, section: 'Integrations' },
      // Phase A - Per-tenant JWT verifier registry (drives /v1/auth/exchange).
      { pageId: 'disc.adm_jwt', to: '/admin/auth/jwt-verifiers', label: 'JWT Verifiers', icon: KeyRound, section: 'Integrations' },
      { pageId: 'disc.adm_idp', to: '/admin/auth/idp', label: 'SSO / IdP (SAML/OIDC)', icon: KeyRound, section: 'Integrations' },
      { pageId: 'disc.adm_ldap', to: '/admin/auth/ldap', label: 'LDAP / Active Directory', icon: KeyRound, section: 'Integrations' },
      { pageId: 'disc.adm_scim', to: '/admin/auth/scim', label: 'SCIM Provisioning', icon: UsersIcon, section: 'Integrations' },
      { pageId: 'disc.adm_notify', to: '/admin/notify', label: 'Notifications (Email/SMS)', icon: Megaphone, section: 'Integrations' },
      { pageId: 'disc.adm_consent_source', to: '/admin/consent-source', label: 'Consent Source', icon: Plug, section: 'Integrations' },
      { pageId: 'disc.adm_pan', to: '/admin/pan-verifier', label: 'PAN Verifier', icon: BadgeCheck, section: 'Integrations' },
      { pageId: 'disc.adm_qr', to: '/admin/qr-verification', label: 'Identity QR', icon: ScanLine, section: 'Integrations' },
      { pageId: 'disc.adm_bus', to: '/admin/business-units', label: 'Business Units', icon: Building2, section: 'Identity & access' },
      { pageId: 'disc.adm_bu_source', to: '/admin/bu-source', label: 'Business Unit Import', icon: Plug, section: 'Identity & access' },
      { pageId: 'disc.adm_purposes', to: '/admin/purposes', label: 'Purposes', icon: Workflow, section: 'Identity & access' },
      // T1.5.B (2026-05-24) - DPDPA §8 DPO + §13 grievance officer surfaced from Core.
      { pageId: 'disc.adm_dpo_grievance', to: '/admin/dpo-grievance', label: 'DPO & Grievance', icon: ShieldCheck, badge: 'DPDPA', section: 'Identity & access' },
      // F (2026-05-25) - Unified compliance posture across all 7 federation entities.
      { pageId: 'disc.adm_posture', to: '/admin/compliance-posture', label: 'Compliance Posture', icon: ShieldCheck, section: 'Identity & access' },
      // Phase 5b items 4 / 6 / 9 / 7 - RBAM matrix · Purposes · Adapters · Licenses.
      { pageId: 'disc.adm_rbam', to: '/admin/rbam', label: 'Role Permissions', icon: ShieldCheck, section: 'Identity & access' },
      { pageId: 'disc.adm_custom_roles', to: '/admin/custom-roles', label: 'Custom Roles', icon: KeyRound, badge: '2P', section: 'Identity & access' },
      // T1.1 (2026-05-28) - Discovery-native user management. Backend
      // shipped in c409666; UI shipped to close the Discovery Independence
      // Principle gap (CLAUDE.md §14). Standalone customers no longer
      // need a Core deployment to manage their own users.
      { pageId: 'disc.adm_users', to: '/admin/users', label: 'Users', icon: UsersIcon, section: 'Identity & access' },
      // Machine credentials (gateway, DSAR connector, Core, web proxy): list and revoke.
      { pageId: 'disc.adm_service_creds', to: '/admin/service-credentials', label: 'Service credentials', icon: KeyRound, section: 'Identity & access' },
      // T1.2 (2026-05-28) - Tenant-facing aggregate health snapshot.
      // Audit chain + scanners + edge fleet + audit-vault + recent
      // errors + compound correlator in one read.
      { pageId: 'disc.adm_health', to: '/admin/health', label: 'System Health', icon: Server, section: 'Edge & audit' },
      // T1.4 (2026-05-28) - AuditSink config (SIEM webhook / Splunk
      // HEC etc.). Secondary destination; the per-tenant DB audit chain
      // is always the primary persistence.
      { pageId: 'disc.adm_audit_sink', to: '/admin/audit-sink', label: 'SIEM Forwarding', icon: Plug, section: 'Edge & audit' },
      { pageId: 'disc.adm_adapters', to: '/admin/adapters', label: 'Adapters', icon: Plug, section: 'Policy & catalog' },
      { pageId: 'disc.adm_licences', to: '/admin/licenses', label: 'Licenses', icon: Award, section: 'Policy & catalog' },
      { pageId: 'disc.adm_flags', to: '/admin/feature-flags', label: 'Feature Flags', icon: Layers, section: 'Policy & catalog' },
      // Phase 5 (2026-05-22) - DAM retention policy. Per-tenant, industry-default-aware.
      { pageId: 'disc.adm_retention', to: '/admin/retention-policy', label: 'DAM Retention', icon: Timer, badge: '7y', section: 'Policy & catalog' },
      // Phase 6 - Industry context for multi-industry tenants.
      { pageId: 'disc.adm_packs', to: '/admin/industry-packs', label: 'Industry Packs', icon: Layers, badge: 'India', section: 'Policy & catalog' },
      { pageId: 'disc.adm_industry_overview', to: '/admin/industry-overview', label: 'Industry Overview', icon: Layers, section: 'Policy & catalog' },
      { pageId: 'disc.adm_effective', to: '/admin/effective-config', label: 'Effective Config', icon: Layers, section: 'Policy & catalog' },
      { pageId: 'disc.adm_notif_prefs', to: '/admin/notification-preferences', label: 'Notification preferences', icon: Atom, section: 'Identity & access' },
      { pageId: 'disc.adm_audit_logs', to: '/admin/audit-logs', label: 'Audit & Logs', icon: History, section: 'Edge & audit' },
      { pageId: 'disc.adm_vocab', to: '/admin/classifier-vocab', label: 'Classifier Vocab', icon: Hash, section: 'Policy & catalog' },
      { pageId: 'disc.adm_moats', to: '/admin/india-moats', label: 'India Moats', icon: KeyRound, section: 'Policy & catalog' },
      // Phase 7 - Edge Ops (fleet view + provisioning wizard).
      { pageId: 'disc.adm_edge_fleet', to: '/admin/edge-fleet', label: 'Agent Management', icon: Server, section: 'Edge & audit' },
      { pageId: 'disc.adm_entitlement', to: '/admin/agent-entitlement', label: 'Agent Licenses', icon: Server, section: 'Edge & audit' },
      { pageId: 'disc.adm_edge_prov', to: '/admin/edge-provisioning', label: '→ Deploy agent', icon: Cpu, section: 'Edge & audit' },
      // Phase 5b item 8 - Hash-chained egress log for CISO tamper-evidence audits.
      { pageId: 'disc.adm_egress', to: '/admin/egress-log', label: 'Egress Log', icon: FileLock2, section: 'Edge & audit' },
      // Phase 0 cleanup (2026-05-26) - removed misleading entries for
      // `/admin/api-keys` and `/admin/audit`. The previous comment claimed
      // these were "shipped surfaces" but verify-before-fix grep on
      // 2026-05-26 confirmed: zero router.tsx entries + zero page files.
      // Clicking either → 404. Re-introduce only when the actual pages
      // land alongside their router entries.
      // 2026-05-27 - Onboarding checklist moved here from a top-
      // level Welcome & Setup pill. Getting-started surface, not
      // daily-ops, so it lives at the end of the admin list.
      { pageId: 'disc.adm_welcome', to: '/welcome', label: 'Onboarding checklist', icon: Sparkles, section: 'Edge & audit' },
    ],
  },
];

/**
 * The environment pill in the sidebar footer, from the hostname.
 *
 * A customer's own hostname used to fall through to 'preview', so a bank's
 * production console (discovery.<bank>.com) called itself a preview build.
 * Only names that say what they are get a non-production label; everything
 * else is someone's production install.
 */
/**
 * The groups and items offered to `caller`, in sidebar order (release 3.1,
 * D6). Nothing while the caller is unknown (loading, or GET /v1/me failed):
 * fail closed. Exported for the generated sidebar-equals-spec test.
 */
export function visibleNav(caller: Caller | null): NavGroup[] {
  if (!caller) return [];
  return NAV.map((g) => ({ ...g, items: g.items.filter((item) => canOpen(item.pageId, caller)) })).filter(
    (g) => g.items.length > 0,
  );
}

/**
 * The module each sold group stands for, for the "not licensed" lines.
 * Frontier (no tenant holds it by default) and Settings & Admin (no licence)
 * are left out on purpose: they are not modules a customer is sold.
 */
const PLAN_GROUP_MODULE: Partial<Record<NavGroup['id'], string>> = {
  discovery: 'discovery',
  activity: 'activity',
  detection: 'ddr',
  compliance: 'compliance',
};

/**
 * The sold groups the caller's ROLE would open but the tenant's PLAN does not
 * include (UAT #215): every item refused only for its licence. A DPO on the
 * Professional plan gets Detection and Activity here, so the sidebar and Jump
 * to can say the module is not in the plan instead of showing nothing. A page
 * the role may not open (or a permission it lacks) is never listed: this only
 * explains licences, it never advertises another role's pages. Nothing while
 * the caller or the licences are unknown, and nothing when billing is
 * cancelled: then the plan includes the modules and the remedy is to
 * reinstate the subscription (LicenceStatusNotice says so), not to add one.
 */
export function notInPlanNav(caller: Caller | null, billingCancelled = false): Array<NavGroup & { module: string }> {
  if (!caller || caller.modules === null || billingCancelled) return [];
  const out: Array<NavGroup & { module: string }> = [];
  for (const g of NAV) {
    const module = PLAN_GROUP_MODULE[g.id];
    if (!module) continue;
    const items = g.items.filter((item) => {
      const page = pageById(item.pageId);
      if (!page) return false;
      const decision = decidePage(page, caller);
      return !decision.allowed && decision.reason === 'licence';
    });
    if (items.length > 0) out.push({ ...g, items, module });
  }
  return out;
}

export function environmentLabel(host: string): string {
  const h = host.toLowerCase();
  if (!h || h.includes('localhost') || h.endsWith('.local') || h === '127.0.0.1') return 'dev';
  // 'uat' ending a label ("motilaloswaluat.com", "app-uat.x") or starting one
  // ("uat.x", "uat-app.x"); not inside a word ("graduate", "evaluation").
  if (/uat([.-]|$)|(^|[.-])uat/.test(h)) return 'uat';
  if (h.includes('staging')) return 'staging';
  if (h.includes('preview')) return 'preview';
  return 'production';
}

/**
 * The entry an address is on: the one whose route is the address or its longest
 * parent, so /dspm/scans/<id> marks Scans and not also Dashboard.
 *
 * A group's landing route (/dspm, /compliance) is one segment and is only its
 * own page: /dspm/honey-pii has no entry, and marking Dashboard for it would
 * name a page the person is not on.
 */
export function activeNavItem(items: readonly NavItem[], path: string): NavItem | null {
  let best: NavItem | null = null;
  for (const item of items) {
    const parent = item.to.split('/').length > 2 && path.startsWith(`${item.to}/`);
    if ((path === item.to || parent) && (!best || item.to.length > best.to.length)) best = item;
  }
  return best;
}

/**
 * A group's entries by section, in the order each section first appears, or
 * null for a group with no sections. Settings & Admin lists its sections
 * interleaved; listed flat that printed "Identity & access" twice.
 */
function sectionsOf(items: readonly NavItem[]): Array<[string, NavItem[]]> | null {
  if (!items.some((i) => i.section)) return null;
  const bySection = new Map<string, NavItem[]>();
  for (const item of items) {
    const key = item.section ?? 'More';
    bySection.set(key, [...(bySection.get(key) ?? []), item]);
  }
  return [...bySection];
}

/**
 * The pages "Jump to" offers for what was typed: every word must start a word
 * of the page's name, its section or its group. Matching at word starts keeps
 * "sso" from finding every name with those letters inside it.
 */
export function jumpResults(
  groups: readonly NavGroup[],
  query: string,
  limit = 8,
): Array<{ item: NavItem; group: NavGroup }> {
  // Names and what is typed are reduced the same way, so a name typed as it is
  // printed ("break-glass", "siem / soar") finds its page.
  const plain = (text: string) => text.toLowerCase().replace(/[^a-z0-9&]+/g, ' ').trim();
  const typed = plain(query).split(' ').filter(Boolean);
  if (typed.length === 0) return [];
  const startsWord = (text: string, w: string) => ` ${text}`.includes(` ${w}`);
  const found: Array<{ item: NavItem; group: NavGroup; score: number }> = [];
  for (const group of groups) {
    for (const item of group.items) {
      const label = plain(item.label);
      const around = plain(`${item.section ?? ''} ${group.label}`);
      if (!typed.every((w) => startsWord(label, w) || startsWord(around, w))) continue;
      const score = (label.startsWith(typed[0] ?? '') ? 2 : 0) + (typed.every((w) => startsWord(label, w)) ? 1 : 0);
      found.push({ item, group, score });
    }
  }
  return found.sort((a, b) => b.score - a.score).slice(0, limit);
}

const EXACT = { exact: true } as const;

export function SideNav({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const router = useRouterState();
  const path = router.location.pathname;
  const { entitlements } = useEntitlements();
  // Release 3.1 persona D6: the caller's role, grants (GET /v1/me) and
  // licences decide every item, from the generated persona table. While any
  // of them is unknown the sidebar offers nothing (it used to offer every
  // item while loading, and everything when the licences failed to load).
  const access = useCaller();
  const caller = access.status === 'ready' ? access.caller : null;
  // P1 OBS-3.alt - live bootstrap-progress badge (e.g. "5/8" instead
  // of the static "L1-L8" descriptor).
  const bootstrapBadge = useBootstrapBadge(canOpen('disc.dspm_bootstrap', caller));

  // Inject the live bootstrap badge into the 8-Level Bootstrap nav item at
  // render time so the descriptor reflects state.
  const filteredNav = useMemo(
    () =>
      visibleNav(caller).map((g) => ({
        ...g,
        items: g.items.map((item) => (item.to === '/dspm/bootstrap' ? { ...item, badge: bootstrapBadge } : item)),
      })),
    [caller, bootstrapBadge],
  );
  const homeVisible = canOpen('disc.home', caller);
  // UAT #215: the sold modules this role would open but the plan does not
  // include, said in the sidebar and in Jump to instead of left unexplained.
  const billingCancelled = entitlements?.billingStatus === 'cancelled';
  const notInPlan = useMemo(() => notInPlanNav(caller, billingCancelled), [caller, billingCancelled]);
  const plan = planLabel(entitlements?.sku);
  // Release 3.1 persona parity (founder 27 Sep): the FOCTTA TPRM sidebar
  // entry, directly under Home. Null unless Discovery is paired with Core,
  // Core says TPRM's address, and this role is on the persona table's
  // disc.tprm_link list (see lib/use-connected-products.ts).
  const tprm = useTprmLink();

  // Map a URL pathname to its owning section. `/welcome` is now an
  // Admin sub-item; every other top-level segment matches a group id
  // directly. Default to 'discovery' so the post-login Home (/)
  // highlights Discovery rather than nothing.
  function groupFromPath(p: string): NavGroup['id'] {
    const top = p.split('/')[1] ?? '';
    if (top === 'welcome') return 'admin';
    const match = NAV.find((g) => g.id === top);
    return match?.id ?? 'discovery';
  }

  const pathGroup = groupFromPath(path);
  const activeItem = useMemo(() => activeNavItem(filteredNav.flatMap((g) => g.items), path), [filteredNav, path]);

  // One group open at a time and, inside it, one section, so the sidebar fits
  // one screen: the flat list was 35 entries long under Settings & Admin. The
  // group and section holding the page open on every navigation; what is
  // opened by hand stays until the next one.
  const [openGroup, setOpenGroup] = useState<NavGroup['id'] | null>(pathGroup);
  // Which section starts open is read from the full list, so the sidebar does
  // not paint with every section shut and then open one. What is offered still
  // comes only from the caller's own pages below.
  const [openSection, setOpenSection] = useState<string | null>(
    () =>
      activeNavItem(NAV.flatMap((g) => g.items), path)?.section ??
      NAV.find((g) => g.id === pathGroup)?.items[0]?.section ??
      null,
  );
  // Keyed, because the nav is a new array on every render and re-running then
  // would undo a group the person has just opened. The length is in the key so
  // the sidebar settles once more when the caller's pages arrive.
  const settledFor = useRef<string | null>(null);
  useEffect(() => {
    const key = `${path}|${activeItem?.pageId ?? ''}|${filteredNav.length}`;
    if (settledFor.current === key) return;
    settledFor.current = key;
    // A role with no page in the path's group gets its first group, as before.
    const group = filteredNav.find((g) => g.id === pathGroup) ?? filteredNav[0];
    setOpenGroup(group?.id ?? pathGroup);
    setOpenSection(activeItem?.section ?? group?.items[0]?.section ?? null);
  }, [path, activeItem, filteredNav, pathGroup]);

  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const results = useMemo(() => jumpResults(filteredNav, query), [filteredNav, query]);
  const planResults = useMemo(() => jumpResults(notInPlan, query), [notInPlan, query]);
  // A page was opened some other way (Home, the palette): the search is over.
  useEffect(() => {
    setQuery('');
  }, [path]);

  // "/" focuses Jump to from anywhere that is not already a text field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing =
        t instanceof HTMLInputElement ||
        t instanceof HTMLTextAreaElement ||
        t instanceof HTMLSelectElement ||
        t?.isContentEditable;
      if (e.key !== '/' || typing || e.metaKey || e.ctrlKey || e.altKey) return;
      // No box to focus (icon rail, or no pages): the key is not ours to take.
      // The same while a dialog, menu or list is open over the page.
      if (!searchRef.current) return;
      if (document.querySelector('[role="dialog"], [role="menu"], [role="listbox"]')) return;
      e.preventDefault();
      searchRef.current.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const railGroup = openGroup ?? pathGroup;

  const pageLink = (item: NavItem, withIcon: boolean) => {
    const Icon = item.icon;
    const active = item.pageId === activeItem?.pageId;
    // Only a badge that reports state is shown. The fixed tags ("BFSI", "5x",
    // "29") said nothing a person could act on and pushed names off the row.
    const live = item.to === '/dspm/bootstrap' && item.badge !== 'L1-L8' ? item.badge : undefined;
    return (
      <Link
        key={item.pageId}
        to={item.to}
        data-nav-id={item.pageId}
        // The router's own "current page" mark is fuzzy: on /dspm/scans it also
        // marked Dashboard (/dspm). Exact, so the one mark is the one set here.
        activeOptions={EXACT}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] transition-colors',
          active
            ? 'bg-primary font-medium text-primary-foreground'
            : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        {withIcon && <Icon className="h-4 w-4 shrink-0" />}
        <span className="min-w-0 flex-1 truncate" title={item.label}>
          {item.label}
        </span>
        {live && (
          <span
            className={cn(
              'shrink-0 rounded px-1.5 py-0.5 text-[10px] tabular-nums',
              active ? 'bg-primary-foreground/20' : 'glass-lite',
            )}
          >
            {live}
          </span>
        )}
      </Link>
    );
  };

  return (
    <aside
      className={cn(
        // flex-none locks the aside to its explicit w-16/w-64 width.
        // Without it, the flex parent's default `flex: 0 1 auto` lets
        // flex-basis fall back to content size - so the aside would
        // stay at the horizontal-logo's content width (~256px) even
        // when w-16 is supposedly active. flex-none = flex: 0 0 auto
        // forces the browser to respect width property.
        // overflow-hidden + min-w-0 prevent children from forcing the
        // aside back to content width.
        'glass relative flex flex-col flex-none min-w-0 overflow-hidden transition-[width] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Brand header - Phase 0+ (2026-05-26 fix): brand mark + wordmark
          are now a Link to "/" so clicking the FOCTTA logo from any
          deep route returns to the landing page (guided tour, demo,
          operator walkthrough CTAs). The sidebar-collapse toggle moved
          to a separate chevron button on the right edge, matching the
          Linear/Salesforce pattern of brand-clicks-home vs separate
          collapse affordance. */}
      {/* 2026-05-27 - brand area matches FOCTTA Core's side-nav
          pattern: fixed h-14 (56px) header with h-7 (28px) logo.
          Mark uses height-based sizing (size = pixel height) so the
          wide 5:1 wordmark renders at ~140px wide, leaving room for
          the collapse-toggle chevron on the right. */}
      <div
        className={cn(
          'flex h-14 items-center border-b border-border/40 shrink-0',
          collapsed ? 'justify-center px-0' : 'justify-between px-4',
        )}
      >
        <Link
          to="/"
          className="flex items-center min-w-0 hover:opacity-80 transition-opacity rounded-md"
          aria-label="Go to FOCTTA Home"
        >
          {collapsed ? (
            <Mark size={28} animated />
          ) : (
            <Mark variant="wordmark" size={28} animated />
          )}
        </Link>
        {!collapsed && (
          <button
            onClick={onToggle}
            className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        )}
      </div>
      {collapsed && (
        <button
          onClick={onToggle}
          className="absolute top-2 right-1 z-10 text-muted-foreground hover:text-foreground transition-colors p-1 rounded bg-card/80"
          aria-label="Expand sidebar"
          title="Expand sidebar"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </button>
      )}

      {/* Licence state (release 3, licensing audit S5): a module in its grace
          period, read only or cancelled says so here, on every page, before a
          change the user tries is refused with 402. Nothing while in term. */}
      <LicenceStatusNotice licences={entitlements?.licences} collapsed={collapsed} />

      {!collapsed && filteredNav.length > 0 && (
        <div className="px-2 pt-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                // Not while an input method is still composing the word.
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) resultsRef.current?.querySelector('a')?.click();
                if (e.key === 'Escape') {
                  setQuery('');
                  searchRef.current?.blur();
                }
              }}
              placeholder="Jump to..."
              aria-label="Jump to a page"
              data-testid="nav-jump"
              className="h-8 w-full rounded-md border border-border/60 bg-background/60 pl-8 pr-7 text-[13px] placeholder:text-muted-foreground/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {!query && (
              <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-border/60 px-1 text-[10px] text-muted-foreground">
                /
              </kbd>
            )}
          </div>
        </div>
      )}

      {/* Collapsed: the icon rail, one icon per module and per page of the
          module in view, as before. */}
      {collapsed && (
        <div className="px-2 py-3 grid gap-1 grid-cols-1">
          <TooltipProvider delayDuration={200}>
            {filteredNav.map((g) => {
              const Icon = g.icon;
              const active = railGroup === g.id;
              // The icon lands on the group's default page when the caller may
              // open it, else on the first page it may (release 3.1, D6).
              const href =
                g.defaultHref && g.items.some((i) => i.to === g.defaultHref) ? g.defaultHref : (g.items[0]?.to ?? '/');
              return (
                <Tooltip key={g.id}>
                  <TooltipTrigger asChild>
                    <Link
                      to={href}
                      data-nav-id={`disc.group.${g.id}`}
                      onClick={() => setOpenGroup(g.id)}
                      className={cn(
                        'relative flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium transition-all duration-200',
                        active
                          ? 'text-foreground bg-primary/10 shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/40',
                      )}
                    >
                      {active && (
                        <motion.div
                          layoutId="active-module-bg"
                          className={cn('absolute inset-0 rounded-md bg-gradient-to-br opacity-15', g.accent)}
                          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        />
                      )}
                      <Icon className="h-4 w-4 shrink-0 relative z-10" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">{g.label}</TooltipContent>
                </Tooltip>
              );
            })}
          </TooltipProvider>
        </div>
      )}

      {collapsed && <Separator />}

      {/* Featured top-level link - explicit Home. 2026-05-27: Home added per
          user feedback that "click the logo to go home" isn't obvious to
          first-time operators. The Innovation catalogue link that sat here was
          removed in release 3.1 (persona D6): the catalogue is a mock and the
          persona specification hides it from every role. */}
      {homeVisible && (
        <div className={cn('px-2', collapsed ? 'py-2' : 'pt-2')}>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/"
                  data-nav-id="disc.home"
                  aria-current={path === '/' ? 'page' : undefined}
                  className={cn(
                    'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors',
                    path === '/'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted/60',
                  )}
                >
                  <Home className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="flex-1 truncate">Home</span>}
                </Link>
              </TooltipTrigger>
              {collapsed && <TooltipContent side="right">Home</TooltipContent>}
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {/* FOCTTA TPRM shortcut (release 3.1, persona parity, founder 27 Sep):
          mirrors FOCTTA Consent's own sidebar entry, directly under Home.
          Never a dead link - hidden unless useTprmLink() confirms Discovery
          is paired with Core, Core says TPRM's address, and this role is
          allowed (disc.tprm_link). */}
      {tprm && (
        <div className={cn('px-2', collapsed ? 'pb-2' : 'pt-0.5')}>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={tprm.homeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-nav-id="disc.tprm_link"
                  data-testid="sidebar-foctta-tprm"
                  aria-label="FOCTTA TPRM (opens in a new tab)"
                  className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                >
                  <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {!collapsed && (
                    <>
                      <span className="flex-1 truncate">FOCTTA TPRM</span>
                      <ExternalLink className="h-3 w-3 opacity-60" aria-hidden="true" />
                    </>
                  )}
                </a>
              </TooltipTrigger>
              {collapsed && <TooltipContent side="right">FOCTTA TPRM</TooltipContent>}
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {collapsed && <Separator />}

      {collapsed ? (
        <ScrollArea className="flex-1 px-2 py-2">
          <nav className="space-y-0.5">
            <TooltipProvider delayDuration={200}>
              {/* Nothing to show while the caller is unknown, or for a caller
                  with no page at all (release 3.1, D6). */}
              {((filteredNav.find((g) => g.id === railGroup) ?? filteredNav[0])?.items ?? []).map((item) => {
                const Icon = item.icon;
                const active = item.pageId === activeItem?.pageId;
                return (
                  <Tooltip key={item.pageId}>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.to}
                        data-nav-id={item.pageId}
                        activeOptions={EXACT}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'flex items-center justify-center rounded-md px-2.5 py-2 transition-colors',
                          active
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/40',
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                );
              })}
            </TooltipProvider>
          </nav>
        </ScrollArea>
      ) : query.trim() ? (
        <ScrollArea className="flex-1 px-2 py-2">
          <div ref={resultsRef} className="space-y-0.5" data-testid="nav-jump-results">
            {results.length === 0 && planResults.length === 0 ? (
              <p className="px-2 py-3 text-xs text-muted-foreground">No page matches. Try another word.</p>
            ) : (
              results.map(({ item, group }) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.pageId}
                    to={item.to}
                    onClick={() => setQuery('')}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-foreground transition-colors hover:bg-muted/60"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {group.label}
                    </span>
                  </Link>
                );
              })
            )}
            {/* UAT #215: a page of a module the plan does not include is
                named, not linked, with who adds the module. */}
            {planResults.map(({ item, group }) => (
              <div
                key={`plan-${item.pageId}`}
                data-testid="nav-jump-not-in-plan"
                data-plan-page={item.pageId}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-muted-foreground"
              >
                <Lock className="h-4 w-4 shrink-0 text-muted-foreground/70" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                <span className="shrink-0 text-[10px] uppercase tracking-wide">{group.label}: not licensed</span>
              </div>
            ))}
            {[...new Set(planResults.map(({ group }) => group.id))].map((id) => {
              const module = notInPlan.find((g) => g.id === id)?.module ?? id;
              return (
                <p key={`plan-note-${id}`} data-testid="nav-jump-plan-note" className="px-2 py-1.5 text-xs text-muted-foreground">
                  {notInPlanSentence(MODULE_LABELS[module] ?? module, plan)}
                </p>
              );
            })}
          </div>
        </ScrollArea>
      ) : (
        <ScrollArea className="flex-1 px-1.5 py-2">
          <div className="space-y-0.5">
            {filteredNav.map((g) => {
              const Icon = g.icon;
              const open = openGroup === g.id;
              const holdsPage = !!activeItem && g.items.some((i) => i.pageId === activeItem.pageId);
              const sections = sectionsOf(g.items);
              return (
                <div key={g.id} data-open={open} className={cn('rounded-lg px-1', open && 'bg-primary/[0.06] pb-1')}>
                  <button
                    type="button"
                    data-nav-id={`disc.group.${g.id}`}
                    aria-expanded={open}
                    onClick={() => {
                      setOpenGroup(open ? null : g.id);
                      if (!open) setOpenSection(holdsPage ? (activeItem?.section ?? null) : (g.items[0]?.section ?? null));
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left transition-colors',
                      !open && 'hover:bg-muted/60',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    )}
                  >
                    <Icon
                      className={cn('h-4 w-4 shrink-0', open || holdsPage ? 'text-primary' : 'text-muted-foreground/70')}
                    />
                    <span
                      className={cn(
                        'min-w-0 flex-1 truncate text-[11px] font-semibold uppercase tracking-wider',
                        open ? 'text-primary' : holdsPage ? 'text-foreground' : 'text-muted-foreground',
                      )}
                    >
                      {g.label}
                    </span>
                    {!open && holdsPage && (
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" title="You are in this group" aria-hidden="true" />
                    )}
                    <ChevronRight
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 transition-transform',
                        open ? 'rotate-90 text-primary' : 'text-muted-foreground',
                      )}
                      aria-hidden="true"
                    />
                  </button>

                  {/* Only the open group's pages are in the document, as the
                      sidebar-equals-spec test reads them. */}
                  {open && (
                    <nav
                      aria-label={`${g.label} pages`}
                      className="ml-[15px] space-y-0.5 border-l border-primary/30 pl-1.5"
                    >
                      {sections
                        ? sections.map(([section, items]) => {
                            const sectionOpen = openSection === section;
                            const holds = !!activeItem && items.some((i) => i.pageId === activeItem.pageId);
                            return (
                              <div key={section}>
                                <button
                                  type="button"
                                  aria-expanded={sectionOpen}
                                  data-nav-section={section}
                                  onClick={() => setOpenSection(sectionOpen ? null : section)}
                                  className={cn(
                                    'flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] font-medium transition-colors hover:bg-muted/60 hover:text-foreground',
                                    holds ? 'text-foreground' : 'text-muted-foreground',
                                  )}
                                >
                                  <span className="min-w-0 flex-1 truncate text-left">{section}</span>
                                  <ChevronRight
                                    className={cn(
                                      'h-3 w-3 shrink-0 text-muted-foreground/60 transition-transform',
                                      sectionOpen && 'rotate-90',
                                    )}
                                    aria-hidden="true"
                                  />
                                </button>
                                <div hidden={!sectionOpen} className="ml-2 space-y-0.5 border-l border-border pl-1.5">
                                  {items.map((item) => pageLink(item, false))}
                                </div>
                              </div>
                            );
                          })
                        : g.items.map((item) => pageLink(item, true))}
                    </nav>
                  )}
                </div>
              );
            })}
            {/* UAT #215: the sold modules this role would open but the plan
                does not include, so their absence is explained, not silent. */}
            {notInPlan.length > 0 && (
              <div data-testid="nav-not-in-plan" className="mt-3 space-y-1 border-t border-border/40 px-2 pt-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                  Not licensed{plan ? ` (your plan: ${plan})` : ''}
                </p>
                {notInPlan.map((g) => {
                  const Icon = g.icon;
                  const sentence = notInPlanSentence(MODULE_LABELS[g.module] ?? g.module, plan);
                  return (
                    <div
                      key={g.id}
                      data-plan-group={g.id}
                      title={sentence}
                      className="flex items-center gap-2 rounded-md px-1 py-1 text-[12px] text-muted-foreground"
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate">{MODULE_LABELS[g.module] ?? g.label}</span>
                      <Lock className="h-3 w-3 shrink-0 text-muted-foreground/60" aria-hidden="true" />
                    </div>
                  );
                })}
                <p className="px-1 text-[11px] text-muted-foreground">To add a module, contact {SUPPORT_EMAIL}.</p>
              </div>
            )}
          </div>
        </ScrollArea>
      )}

      {/* P1-14 (2026-05-26) - footer with real build provenance.
          Version = short git SHA baked at build time. Env derived
          from hostname (so a single bundle can serve prod + dev
          tenant subdomains and still self-identify). Last deploy
          = ISO timestamp from vite.config.ts `define`. */}
      <div className="px-3 py-2 border-t border-border/40 text-[10px] text-muted-foreground">
        {!collapsed ? (
          <div className="flex items-center justify-between gap-2" title={`Bundle built at ${__BUILD_TIME_ISO__}`}>
            <span className="truncate" title="Short git SHA of the deployed bundle">
              {__BUILD_GIT_SHA__}
            </span>
            <span className="shrink-0 text-muted-foreground/70">
              {(() => {
                try {
                  const t = new Date(__BUILD_TIME_ISO__);
                  const now = Date.now();
                  const minutes = Math.round((now - t.getTime()) / 60_000);
                  if (minutes < 1) return 'just now';
                  if (minutes < 60) return `${minutes}m ago`;
                  const hours = Math.round(minutes / 60);
                  if (hours < 24) return `${hours}h ago`;
                  const days = Math.round(hours / 24);
                  return `${days}d ago`;
                } catch {
                  return __BUILD_TIME_ISO__.slice(0, 10);
                }
              })()}
            </span>
            <span className="flex shrink-0 items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
              {environmentLabel(typeof window !== 'undefined' ? window.location.hostname : '')}
            </span>
          </div>
        ) : (
          <span className="block h-1.5 w-1.5 rounded-full bg-success animate-pulse mx-auto" />
        )}
      </div>
    </aside>
  );
}
